import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { X, Star, MapPin, ShieldCheck, Lightning, Check, Minus, ArrowRight } from '@phosphor-icons/react';
import { providerPrice } from '@/lib/pricing';
import { CANCELLATION_POLICIES } from '@/lib/policies';

// Side-by-side vendor comparison. Two pieces:
//  • CompareTray  — a floating bar that appears once ≥1 vendor is selected, with
//    thumbnails, a "Compare (N)" button, and clear.
//  • CompareModal — a full-screen table comparing the picked vendors row-by-row.
// Pure presentation over already-fetched provider objects — no new backend.

const backendUrl = (u) =>
  u && u.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;

const cardImage = (p) => {
  const raw = p.cover_image || (p.portfolio_images && p.portfolio_images[0]) || p.user?.avatar_url;
  return raw ? backendUrl(raw) : null;
};

const policyLabel = (p) =>
  (CANCELLATION_POLICIES[p.cancellation_policy] || CANCELLATION_POLICIES.flexible || { label: 'Flexible' }).label;

export function CompareTray({ items, onRemove, onClear, onOpen, max = 4 }) {
  if (!items.length) return null;
  return createPortal(
    <div className="fixed bottom-0 inset-x-0 z-[80] px-3 pb-3 pointer-events-none" data-testid="compare-tray">
      <div className="pointer-events-auto max-w-4xl mx-auto bg-card border border-border shadow-lift rounded-2xl px-3 py-2.5 flex items-center gap-3">
        <span className="text-xs font-semibold text-muted-foreground shrink-0 pl-1 hidden sm:block">
          Compare
        </span>
        <div className="flex items-center gap-2 overflow-x-auto flex-1 min-w-0">
          {items.map((p) => {
            const img = cardImage(p);
            return (
              <div key={p.profile_id} className="relative shrink-0 group">
                <div className="w-11 h-11 rounded-lg overflow-hidden bg-secondary/60 border border-border">
                  {img
                    ? <img src={img} alt={p.business_name} className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center text-lg">✨</div>}
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(p)}
                  aria-label={`Remove ${p.business_name} from compare`}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-foreground text-background flex items-center justify-center shadow-soft btn-anim cursor-pointer">
                  <X size={11} weight="bold" />
                </button>
              </div>
            );
          })}
          {Array.from({ length: Math.max(0, max - items.length) }).map((_, i) => (
            <div key={`slot-${i}`} className="shrink-0 w-11 h-11 rounded-lg border-2 border-dashed border-border/70" aria-hidden="true" />
          ))}
        </div>
        <button
          type="button" onClick={onClear}
          className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors shrink-0 cursor-pointer">
          Clear
        </button>
        <button
          type="button" onClick={onOpen}
          disabled={items.length < 2}
          className="shrink-0 inline-flex items-center gap-1.5 bg-primary text-primary-foreground text-xs font-semibold rounded-xl px-4 h-10 btn-anim shadow-soft hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          data-testid="compare-open">
          Compare {items.length > 1 ? `(${items.length})` : ''}
        </button>
      </div>
      {items.length < 2 && (
        <p className="text-center text-[11px] text-muted-foreground mt-1.5">Pick one more to compare</p>
      )}
    </div>,
    document.body
  );
}

// One comparison row: a label + a cell renderer per vendor. `best` optionally
// highlights the strongest value (e.g. highest rating) with a subtle ring.
function Row({ label, items, render, bestIndex }) {
  return (
    <tr className="border-t border-border/60">
      <th scope="row" className="text-left align-top py-3 pr-3 pl-4 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap sticky left-0 bg-card z-10">
        {label}
      </th>
      {items.map((p, i) => (
        <td key={p.profile_id}
          className={`align-top py-3 px-3 text-sm text-foreground min-w-[160px] ${i === bestIndex ? 'bg-primary/5' : ''}`}>
          {render(p, i === bestIndex)}
        </td>
      ))}
    </tr>
  );
}

export function CompareModal({ items, onClose, onRemove }) {
  // Lock the background from scrolling while the overlay is up, and let Escape
  // close it — mirrors the app's Lightbox. Hooks must run before any early return.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // Comparison needs at least two vendors. If in-modal removal drops it to one
  // (or zero), close instead of showing a nonsensical one-vendor "comparison"
  // where that lone vendor is tagged both Lowest price and best rating.
  useEffect(() => {
    if (items.length < 2) onClose();
  }, [items.length, onClose]);
  if (items.length < 2) return null;

  // Compute "best" indices for the highlightable numeric rows.
  const ratings = items.map((p) => (p.review_count > 0 ? Number(p.rating_avg) || 0 : -1));
  const bestRating = ratings.some((r) => r > 0) ? ratings.indexOf(Math.max(...ratings)) : -1;

  const prices = items.map((p) => {
    const pr = providerPrice(p);
    return pr.isQuote ? Infinity : (pr.amount || Infinity);   // quote = "no fixed price", not cheapest
  });
  const bestPrice = prices.some((v) => v !== Infinity) ? prices.indexOf(Math.min(...prices)) : -1;

  return createPortal(
    <div className="fixed inset-0 z-[95] bg-foreground/60 backdrop-blur-sm flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto fade-up"
      onClick={onClose} role="dialog" aria-modal="true" aria-label="Compare vendors" data-testid="compare-modal">
      <div className="bg-card rounded-2xl shadow-lift w-full max-w-4xl my-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="text-lg font-display font-semibold text-foreground">Compare {items.length} pros</h2>
          <button type="button" onClick={onClose} aria-label="Close comparison"
            className="w-9 h-9 rounded-full hover:bg-secondary/70 flex items-center justify-center btn-anim cursor-pointer">
            <X size={18} />
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            {/* Header: photo + name + View link per vendor */}
            <thead>
              <tr>
                <th className="sticky left-0 bg-card z-10 w-28" aria-hidden="true" />
                {items.map((p) => {
                  const img = cardImage(p);
                  return (
                    <th key={p.profile_id} scope="col" className="align-top p-3 min-w-[160px]">
                      <div className="flex flex-col items-center text-center gap-2">
                        <div className="relative">
                          <div className="w-16 h-16 rounded-xl overflow-hidden bg-secondary/60 border border-border">
                            {img
                              ? <img src={img} alt={p.business_name} className="w-full h-full object-cover" />
                              : <div className="w-full h-full flex items-center justify-center text-2xl">✨</div>}
                          </div>
                          <button type="button" onClick={() => onRemove(p)} aria-label={`Remove ${p.business_name}`}
                            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-foreground text-background flex items-center justify-center shadow-soft btn-anim cursor-pointer">
                            <X size={11} weight="bold" />
                          </button>
                        </div>
                        <Link to={`/provider/${p.profile_id}`} className="text-sm font-semibold text-foreground hover:text-primary leading-tight line-clamp-2">
                          {p.business_name}
                        </Link>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              <Row label="Price" items={items} bestIndex={bestPrice} render={(p, best) => {
                const pr = providerPrice(p);
                return (
                  <span className={`font-display font-semibold ${best ? 'text-primary' : ''}`}>
                    {pr.isQuote ? 'Custom quote' : pr.isRange ? pr.display : `$${pr.amount}${pr.suffixShort}`}
                    {best && <span className="block text-[10px] font-sans font-medium text-primary/80 uppercase tracking-wide mt-0.5">Lowest</span>}
                  </span>
                );
              }} />

              <Row label="Rating" items={items} bestIndex={bestRating} render={(p, best) => (
                p.review_count > 0 ? (
                  <span className={`inline-flex items-center gap-1 ${best ? 'font-semibold text-primary' : ''}`}>
                    <Star size={14} weight="fill" className="text-amber-400" />
                    {p.rating_avg} <span className="text-xs text-muted-foreground">({p.review_count})</span>
                  </span>
                ) : <span className="text-xs text-muted-foreground">New — no reviews yet</span>
              )} />

              <Row label="Category" items={items} render={(p) => p.category || '—'} />

              <Row label="Location" items={items} render={(p) => (
                <span className="inline-flex items-center gap-1 text-sm">
                  <MapPin size={13} className="text-primary shrink-0" /> {p.city || '—'}
                </span>
              )} />

              <Row label="Verified" items={items} render={(p) => (
                p.verified
                  ? <span className="inline-flex items-center gap-1 text-emerald-600"><ShieldCheck size={14} weight="fill" /> Verified</span>
                  : <span className="inline-flex items-center gap-1 text-muted-foreground"><Minus size={13} /> Not yet</span>
              )} />

              <Row label="Capacity" items={items} render={(p) => (p.capacity ? `${p.capacity} guests` : '—')} />

              <Row label="Cancellation" items={items} render={(p) => policyLabel(p)} />

              <Row label="Special offer" items={items} render={(p) => (
                p.special_offer
                  ? <span className="inline-flex items-start gap-1 text-accent-coral text-xs font-medium"><Lightning size={13} weight="fill" className="shrink-0 mt-0.5" /> {p.special_offer}</span>
                  : <Minus size={14} className="text-muted-foreground" />
              )} />

              {/* CTA row */}
              <tr className="border-t border-border/60">
                <th scope="row" className="sticky left-0 bg-card z-10" aria-hidden="true" />
                {items.map((p) => (
                  <td key={p.profile_id} className="py-4 px-3">
                    <Link to={`/provider/${p.profile_id}`}
                      className="inline-flex items-center justify-center gap-1.5 w-full bg-primary text-primary-foreground text-xs font-semibold rounded-xl h-10 btn-anim shadow-soft hover:bg-primary/90 no-underline cursor-pointer">
                      View & Book <ArrowRight size={12} />
                    </Link>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>,
    document.body
  );
}
